import {Fade} from '@aboutyou/transitions';
import {styled} from '@linaria/react';
import React, {PropsWithChildren} from 'react';
import {COLORS} from '../../../constants/colors';
import {tabletAndUpLinaria} from '../../../constants/customMedia';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {WithBadge} from '../../atoms/Badge/WithBadge';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {Icon, IconSize} from '../../atoms/Icon/Icon';
import {IconName} from '../../atoms/Icon/IconName';
import {Text} from '../../atoms/Text/Text';
import {UnstyledLink} from '../../atoms/UnstyledLink/UnstyledLink';

export const FADE_DURATION = 300;

export type HeaderButtonProps = PropsWithChildren<{
  asset: IconName;
  onClick?: React.MouseEventHandler;
  count?: number;
  countLabel?: string;
  showBadge?: boolean;
  badgeColor?: string;
  'aria-label': string | undefined;
  badgeOffset?: {right?: number; top?: number};
  to?: string;
  ['data-testid']?: string;
  isCheckout?: boolean;
  iconSize: keyof typeof IconSize;
  trackClick?: VoidFunction;
  className?: string;
  iconRef?: React.Ref<HTMLElement>;
  iconComponent?: React.ReactNode;
  countOffset?: number;
}>;

export const HeaderButton = ({
  asset,
  count,
  countLabel,
  badgeOffset,
  badgeColor,
  showBadge,
  onClick,
  to,
  isCheckout,
  iconSize,
  trackClick,
  iconRef,
  iconComponent,
  'aria-label': ariaLabel,
  countOffset,
  ref,
  ...rest
}: HeaderButtonProps & {ref?: React.Ref<HTMLButtonElement>}) => {
  const props = to
    ? {
        as: UnstyledLink,
        to,
        onClick: trackClick,
      }
    : {
        onClick: (e: React.MouseEvent) => {
          trackClick?.();
          onClick?.(e);
        },
      };

  if (isCheckout) {
    return null;
  }

  return (
    <ButtonBase
      {...props}
      {...rest}
      $iconWidth={IconSize[iconSize]}
      ref={ref}
      aria-label={
        count && countLabel && ariaLabel
          ? `${ariaLabel}, ${countLabel}`
          : ariaLabel || undefined
      }
    >
      {iconComponent || (
        <WithBadge
          variant={'rounded'}
          offset={{
            top: badgeOffset?.top ?? 0,
            right: badgeOffset?.right ?? 0,
          }}
          badgeSize={7}
          badgeColor={badgeColor}
          displayBadgeWithoutCount={showBadge}
          ref={iconRef}
          fadeDuration={FADE_DURATION}
        >
          <Icon
            data-testid="filterFlyoutSectionNextIcon"
            asset={asset}
            fill={COLORS.Black}
            size={iconSize}
          />
        </WithBadge>
      )}
      {Boolean(count) && (
        <Fade mounted duration={FADE_DURATION}>
          <CountText data-testid="numberBadge" $offset={countOffset}>
            {count}
          </CountText>
        </Fade>
      )}
    </ButtonBase>
  );
};

const ButtonBase = styled(UnstyledButton)<{
  $iconWidth: number;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 8px;
  min-width: ${props => props.$iconWidth}px;
  text-align: center;

  ${tabletAndUpLinaria(`
    margin: 0;
    gap: 1px;
  `)}
`;

const CountText = styled(Text)<{$offset?: number}>`
  position: absolute;
  left: ${({$offset}) => ($offset ? `${$offset}px` : '24px')};
  font-size: ${FONT_SIZES.px9};
  font-weight: ${FONT_WEIGHTS.w500};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px10};
    line-height: 10px;
  `)}
`;
